import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Save, Eye } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const EditInformedConsentPage = () => {
  const navigate = useNavigate();
  const { consentId } = useParams();
  const location = useLocation();
  const { toast } = useToast();
  const { practice } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [consent, setConsent] = useState(null);
  const [customName, setCustomName] = useState('');
  const [sections, setSections] = useState({});
  const [showPreview, setShowPreview] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    // Always fetch from API to get cleaned data (signature blocks removed)
    if (consentId) {
      fetchConsent();
    } else {
      navigate('/informed-consent-library');
    }
  }, [consentId]);

  const initializeConsent = (consentData) => {
    setConsent(consentData);
    setCustomName(consentData.customName || consentData.name);
    setSections(consentData.sections || {});
    setLoading(false);
  };

  const fetchConsent = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dentalToken');
      
      const response = await axios.get(
        `${backendUrl}/api/informed-consent/${consentId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        initializeConsent(response.data.data);
      }
    } catch (error) {
      console.error('Fetch consent error:', error);
      toast({
        title: "Error",
        description: "Failed to load informed consent",
        variant: "destructive",
      });
      navigate('/informed-consent-library');
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (sectionKey, value) => {
    setSections(prev => ({
      ...prev,
      [sectionKey]: value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('dentalToken');
      
      await axios.post(
        `${backendUrl}/api/informed-consent/customize`,
        {
          consentId: consent.id,
          name: customName !== consent.name ? customName : null,
          sections: sections
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      toast({
        title: "Success",
        description: "Informed consent customization saved successfully",
      });
      
      navigate('/informed-consent-library');
    } catch (error) {
      console.error('Save customization error:', error);
      toast({
        title: "Error",
        description: `Failed to save customization: ${error.response?.data?.detail || error.message}`,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      toast({
        title: "Generating Preview",
        description: "Creating PDF preview...",
      });

      const token = localStorage.getItem('dentalToken');
      
      // First save the customization
      await axios.post(
        `${backendUrl}/api/informed-consent/customize`,
        {
          consentId: consent.id,
          name: customName !== consent.name ? customName : null,
          sections: sections
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Then generate preview
      const response = await axios.post(
        `${backendUrl}/api/informed-consent/generate-pdf`,
        {
          consentId: consent.id
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Download the preview
      const safeName = (customName || consent.name).replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const filename = `${safeName}_preview.pdf`;
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Preview Downloaded",
        description: "Open the downloaded PDF to view your changes",
      });
      
    } catch (error) {
      console.error('Preview error:', error);
      toast({
        title: "Error",
        description: `Failed to generate preview: ${error.response?.data?.detail || error.message}`,
        variant: "destructive",
      });
    }
  };

  const sectionLabels = {
    procedure: 'Procedure',
    purpose: 'Purpose / Expected Benefits',
    alternatives: 'Alternatives',
    risks: 'Risks and Possible Complications',
    considerations: 'Procedure-Specific Considerations',
    anesthesia: 'Anesthesia/Sedation',
    medicalHistory: 'Medical History Disclosure',
    homeCare: 'Home-Care & Follow-Up',
    imaging: 'Imaging and Records',
    financial: 'Financial Acknowledgment',
    consent: 'Consent and Authorization'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading informed consent...</p>
        </div>
      </div>
    );
  }

  if (!consent) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/informed-consent-library')}
                className="flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Library
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Edit Informed Consent</h1>
                <p className="text-gray-600">{consent.name}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handlePreview}
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview PDF
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Custom Name (Optional)</CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder="Leave blank to use original name"
            />
          </CardContent>
        </Card>

        {Object.entries(sections).map(([key, value]) => (
          <Card key={key} className="mb-6">
            <CardHeader>
              <CardTitle>{sectionLabels[key] || key}</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={value}
                onChange={(e) => handleSectionChange(key, e.target.value)}
                rows={8}
                className="font-mono text-sm"
                placeholder={`Enter ${sectionLabels[key] || key} content...`}
              />
              <p className="text-xs text-gray-500 mt-2">
                You can use placeholders: {'{practiceName}'}, {'{dentistName}'}, {'{patientName}'}, {'{todayDate}'}
              </p>
            </CardContent>
          </Card>
        ))}

        <div className="flex justify-end gap-2 mt-6">
          <Button
            variant="outline"
            onClick={() => navigate('/informed-consent-library')}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditInformedConsentPage;
